import Role, {
  ACCOUNT_PERMISSION_OPTIONS,
  MODEL_STR,
  ALL,
} from "byzantine/src/Role";
import { formatNumber } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import OrganizationUser from "byzantine/src/OrganizationUser";
import OrganizationAccessLevelExtraPermissions from "./OrganizationAccessLevelExtraPermissions";
import { bundleExtraPermissions } from "./OrganizationEditUser";

/** System roles that can't be edited or duplicated, only custom roles can. */
export const NON_CUSTOM_ROLE_NAMES = new Set([
  OrganizationUser.ROLE.ADMIN,
  OrganizationUser.ROLE.COLLABORATOR,
  OrganizationUser.ROLE.VIEWER,
  OrganizationUser.ROLE.ACCOUNT_HOLDER,
  "No Permissions",
]);

// Helper to parse formatted currency strings (removes commas, etc.)
const parseLimitValue = (value: string): number => {
  // Remove formatting characters (commas, spaces, etc.) and parse
  const cleaned = value.replace(/[^\d.-]/g, "");
  return parseFloat(cleaned) || 0;
};

// Helper to safely extract limit amount from nested structure
// Handles both API format { "1": { amount: 5000 } } and internal format { 1: 5000 }
const getLimitAmount = (limits: any, limitType: string): number => {
  const limitEntry = limits?.[limitType];
  if (!limitEntry) return 0;

  const value = limitEntry["1"] || limitEntry[1];
  if (typeof value === "number") return value;
  if (typeof value === "object" && value?.amount) return value.amount;
  return 0;
};

export const getDefaultLimits = (
  features: Record<string, any> | undefined
) => ({
  ach: features?.limits?.ach_push?.[1]
    ? formatNumber(features.limits.ach_push[1])
    : "",
  wire: features?.limits?.wire?.[1]
    ? formatNumber(features.limits.wire[1])
    : "",
  achPayment: features?.limits?.unverified_ach_push?.[1]
    ? formatNumber(features.limits.unverified_ach_push[1])
    : "",
  collectAchPayment: features?.limits?.unverified_ach_pull?.[1]
    ? formatNumber(features.limits.unverified_ach_pull[1])
    : "",
  fednowUnverifiedSend: features?.limits?.fednow_unverified?.[1]
    ? formatNumber(features.limits.fednow_unverified[1])
    : "",
  internationalWire: features?.limits?.international_wire?.[1]
    ? formatNumber(features.limits.international_wire[1])
    : "",
  achPassthroughSend: features?.limits?.ach_passthrough_send?.[1]
    ? formatNumber(features.limits.ach_passthrough_send[1])
    : "",
  achPassthroughCollect: features?.limits?.ach_passthrough_collect?.[1]
    ? formatNumber(features.limits.ach_passthrough_collect[1])
    : "",
});

export const getOrganizationSecCodes = (
  features: Record<string, any> | undefined
) => ({
  push: Object.keys(features?.limits?.unverified_ach_push_by_sec_code ?? {}),
  pull: Object.keys(features?.limits?.unverified_ach_pull_by_sec_code ?? {}),
});

const SEC_CODE_LIMIT_KEYS = {
  push: "unverified_ach_push_by_sec_code",
  pull: "unverified_ach_pull_by_sec_code",
} as const;

const SEC_CODE_RAILS = {
  push: "unverified_ach_push",
  pull: "unverified_ach_pull",
} as const;

type SecCodeDirection = keyof typeof SEC_CODE_LIMIT_KEYS;
type SecCodeLimits = Record<SecCodeDirection, Record<string, string>>;
type SecCodeLimitEnabled = Record<
  SecCodeDirection,
  Record<string, boolean>
>;

export type AccountPermissionState = {
  access: API.AccountAccess;
  /** Account-scoped permission model strings, only meaningful for `move_money`. */
  permissions: string[];
};

export interface FutureAccountsState {
  /** "Access to all accounts opened later" is checked. */
  enabled: boolean;
  /** Uuid of the account whose permissions apply to accounts opened later, or "" when none is picked. */
  referenceId: string;
}

export interface RoleFormData {
  role_name: string;
  role_description: string;
  accounts: any[];
  externalAccounts: any[];
  extraPermissions: Record<string, boolean>;
  ach_limits: string;
  wire_limits: string;
  ach_payment_limits: string;
  collect_ach_payment_limits: string;
  fednow_unverified_send_limits: string;
  international_wire: string;
  ach_passthrough_send_limits: string;
  ach_passthrough_collect_limits: string;
  secCodeLimits?: SecCodeLimits;
  secCodeLimitEnabled?: SecCodeLimitEnabled;
  /** Per-account access and scoped permissions, keyed by account uuid. Missing = not configured yet. */
  accountPermissions: Record<string, AccountPermissionState>;
  /** "Access to all accounts opened later" and the account whose permissions it uses. */
  futureAccounts: FutureAccountsState;
  [MODEL_STR.ACCOUNT]?: boolean;
  [MODEL_STR.EXTERNAL_ACCOUNT]?: boolean;
  [key: string]: any;
}

/**
 * Applies the rules that tie other fields to the account selection, in the same update that changes `accounts`
 * - "All accounts" is on exactly when every account is selected.
 * - Card management and statements aren't scoped to selected accounts, so they can't stay on once "All accounts" is off.
 * - Updates that don't touch `accounts` are returned unchanged.
 */
export const reconcileAccountFields = (
  update: Record<string, any>
): Record<string, any> => {
  if (!Array.isArray(update.accounts)) return update;
  const allSelected =
    update.accounts.length > 0 &&
    update.accounts.every(
      (account: { selected?: boolean }) => account.selected
    );
  return {
    ...update,
    [MODEL_STR.ACCOUNT]: allSelected,
    ...(allSelected
      ? {}
      : { [MODEL_STR.CARD]: false, [MODEL_STR.DOCUMENTS]: false }),
  };
};

/** A newly selected account starts with move money access and nothing chosen ("Needs set up"). */
export const DEFAULT_ACCOUNT_PERMISSION_STATE: AccountPermissionState = {
  access: "move_money",
  permissions: [],
};

/** An account's state in the form. A selected account never configured is Move money with nothing chosen. */
export const getAccountPermissionState = (
  accountPermissions: Record<string, AccountPermissionState> | undefined,
  accountId: unknown
): AccountPermissionState =>
  accountPermissions?.[String(accountId)] ?? DEFAULT_ACCOUNT_PERMISSION_STATE;

export const DEFAULT_FUTURE_ACCOUNTS_STATE: FutureAccountsState = {
  enabled: false,
  referenceId: "",
};

export const ROLE_NAME_MAX_LENGTH = 32;
export const ROLE_DESCRIPTION_MAX_LENGTH = 180;

/** Codes `validateRoleAccounts` can return; `RoleAccountsSection` renders the matching banner. */
export const ACCOUNTS_ERROR = {
  NONE_SELECTED: "none-selected",
  NEEDS_SETUP: "needs-setup",
} as const;

/** True for a selected account set to Move money without any permissions chosen yet. */
export const accountNeedsSetup = (
  account: { selected?: boolean },
  state: AccountPermissionState = DEFAULT_ACCOUNT_PERMISSION_STATE
) =>
  Boolean(account.selected) &&
  state.access === "move_money" &&
  state.permissions.length === 0;

/**
 * The only account-level permissions a loan account can have: it can make internal and external
 * transfers (loan payments), but not send ACH payments, wires or anything else.
 */
export const LOAN_ACCOUNT_PERMISSIONS: string[] = [
  MODEL_STR.INTRA_INSTITUTION_TRANSFER,
  MODEL_STR.ACH_TRANSFER,
];

export const isLoanAccount = (account: { isInternalLoan?: () => boolean }) =>
  Boolean(account.isInternalLoan?.());

export const countAccountPermissions = (
  state: AccountPermissionState,
  features: Record<string, any> = {}
): number =>
  new Set(
    ACCOUNT_PERMISSION_OPTIONS.filter(
      (option) =>
        state.permissions.includes(option.modelStr) &&
        option.isAvailable(features)
    ).map((option) => option.parent ?? option.modelStr)
  ).size;

/**
 * Form update for "Copy to other accounts": every target account is selected, if it wasn't yet,
 * and gets the source account's access and permissions, replacing whatever it had. The source and
 * accounts that aren't targets are left as they are.
 */
export const copyAccountPermissions = (
  formData: Pick<RoleFormData, "accounts" | "accountPermissions">,
  sourceAccountId: string,
  targetAccountIds: string[]
): Pick<RoleFormData, "accounts" | "accountPermissions"> => {
  const targets = new Set(
    targetAccountIds.filter((accountId) => accountId !== sourceAccountId)
  );
  const source =
    formData.accountPermissions[sourceAccountId] ??
    DEFAULT_ACCOUNT_PERMISSION_STATE;
  const copied: Record<string, AccountPermissionState> = {};
  const loanIds = new Set(
    formData.accounts
      .filter((account) => isLoanAccount(account))
      .map((account) => String(account.id))
  );
  targets.forEach((accountId) => {
    copied[accountId] = {
      access: source.access,
      // A loan account can't take permissions it doesn't offer, so it gets the subset it can.
      permissions: loanIds.has(accountId)
        ? source.permissions.filter((modelStr) =>
            LOAN_ACCOUNT_PERMISSIONS.includes(modelStr)
          )
        : [...source.permissions],
    };
  });
  return {
    accounts: formData.accounts.map((account) => {
      if (!targets.has(String(account.id)) || account.selected) return account;
      const selected = new Account(account) as Account & { selected?: boolean };
      selected.selected = true;
      return selected;
    }),
    accountPermissions: { ...formData.accountPermissions, ...copied },
  };
};

/**
 * Validation for the legacy account checklist, which only has account selection (no per-account
 * permissions): at least one account must be selected.
 */
export const validateAccounts = (value: any[]) => {
  const hasSelectedOption = value.some((account) => account.selected);
  if (hasSelectedOption) return null;
  return "Must select at least one account";
};

/**
 * Validates the accounts field of the redesigned role form: at least one account is selected, and
 * every selected account has permissions or is set to View only. Needs `accountPermissions`, so
 * only use it with the form that manages that state. Returns an `ACCOUNTS_ERROR` code, or null
 * when valid.
 */
export const validateRoleAccounts = (
  value: any[],
  data?: { accountPermissions?: Record<string, AccountPermissionState> }
) => {
  const selected = value.filter((account) => account.selected);
  if (selected.length === 0) return ACCOUNTS_ERROR.NONE_SELECTED;
  const hasUnconfiguredAccount = selected.some((account) =>
    accountNeedsSetup(account, data?.accountPermissions?.[String(account.id)])
  );
  return hasUnconfiguredAccount ? ACCOUNTS_ERROR.NEEDS_SETUP : null;
};

/** Returns `message` when `value` is longer than `maxLength`, otherwise null. */
export const validateMaxLength = (
  value: string | undefined,
  maxLength: number,
  message: string
) => ((value ?? "").length > maxLength ? message : null);

/** Field errors the role create and update endpoints return, keyed by backend field name. */
export type RoleFieldErrors = {
  name?: string[];
  description?: string[];
};

const isErrorList = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((item) => typeof item === "string");

/**
 * True for the serializer's field errors, `{ name: ["..."], description: ["..."] }`. The fields must
 * hold error lists: a native `Error` also has a string `name`, and transport failures reject with
 * one.
 */
export const hasRoleFieldErrors = (
  error: unknown
): error is RoleFieldErrors => {
  if (typeof error !== "object" || error === null) return false;
  const { name, description } = error as Record<string, unknown>;
  return isErrorList(name) || isErrorList(description);
};

const isWriteOp = (operation: string) =>
  operation === "write" || operation === "*";

/**
 * Per-account state for a role.
 * - Role without detail (`undefined`, e.g. from the roles list): nothing to derive from.
 */
export const buildAccountPermissionState = (
  role?: Role | null,
  accounts: any[] = []
): Record<string, AccountPermissionState> => {
  if (!role) return {};
  // Role detail with `account_permissions` as an array (converted or omni role): taken as stored.
  if (Array.isArray(role.account_permissions)) {
    return Object.fromEntries(
      role.account_permissions.map((entry) => [
        entry.account_uuid,
        { access: entry.access, permissions: [...entry.permissions] },
      ])
    );
  }
  if (role.account_permissions !== null) return {};

  /**
   *  Role detail with `account_permissions: null` (legacy role): its flat permissions still apply,
   *  so each covered account's access comes from its `indigo.Account` permission, and the
   *  account-level permissions are projected from the role's flat write grants.
   */
  const grantedPermissions = ACCOUNT_PERMISSION_OPTIONS.map(
    (option) => option.modelStr
  ).filter((modelStr) =>
    role.permissions.some(
      (permission) =>
        permission.model_str === modelStr && isWriteOp(permission.operation)
    )
  );
  return Object.fromEntries(
    accounts.flatMap((account) => {
      const accessPermissions = role.permissions.filter(
        (permission) =>
          permission.model_str === MODEL_STR.ACCOUNT &&
          [ALL, String(account.id)].includes(String(permission.uuid))
      );
      if (accessPermissions.length === 0) return [];
      const canMoveMoney = accessPermissions.some((permission) =>
        isWriteOp(permission.operation)
      );
      return [
        [
          String(account.id),
          {
            access: (canMoveMoney
              ? "move_money"
              : "view_only") as API.AccountAccess,
            permissions: canMoveMoney ? [...grantedPermissions] : [],
          },
        ],
      ];
    })
  );
};

/**
 * The existing role's uuid when a save was rejected because another role already has the same
 * permissions and limits, otherwise `null`.
 */
export const getDuplicateRoleUuid = (error: unknown): string | null => {
  if (typeof error !== "object" || error === null) return null;
  const { id, existing_role_uuid: existingRoleUuid } = error as Record<
    string,
    unknown
  >;
  return id === "duplicate_role_configuration" &&
    typeof existingRoleUuid === "string"
    ? existingRoleUuid
    : null;
};

export const toRoleFormErrors = (
  error: unknown,
  messages: { nameExists: string; fallback: string }
) => {
  if (hasRoleFieldErrors(error)) {
    const [nameError] = error.name ?? [];
    return {
      role_name: /already exists/i.test(nameError)
        ? messages.nameExists
        : nameError,
      role_description: error.description?.[0],
    };
  }
  return error || messages.fallback;
};

/**
 * Builds `account_permissions` for the create body: one entry per selected account.
 * - Accounts the role has no access to (not selected) are omitted.
 * - View only accounts always get `permissions: []`, even if permissions were checked before
 *   switching. The form keeps those so switching back restores them, but they are never sent.
 * - Move money accounts send their checked permissions.
 */
export const buildAccountPermissionsPayload = (
  formData: Pick<RoleFormData, "accounts" | "accountPermissions">
): API.AccountPermission[] =>
  formData.accounts
    .filter((account) => account.selected)
    .map((account) => {
      const state = getAccountPermissionState(
        formData.accountPermissions,
        account.id
      );
      return {
        account_uuid: String(
          account.id
        ) as API.AccountPermission["account_uuid"],
        access: state.access,
        permissions:
          state.access === "move_money" ? [...state.permissions] : [],
      };
    });

/**
 * Model strings of the account-level permissions (money movement and manage templates). They are
 * granted per account through `accountPermissions`, never as role-level flags.
 */
const ACCOUNT_SCOPED_MODEL_STRS = new Set(
  ACCOUNT_PERMISSION_OPTIONS.map((option) => option.modelStr)
);

/**
 * Drops the account-level permissions (money movement and manage templates) from the flat
 * permissions list. Those are saved per account through `account_permissions`; a flat grant
 * would apply to every account and override each account's own settings.
 * Account access (`MODEL_STR.ACCOUNT`) is not touched: it stays in the flat list, and the server
 * rebuilds it from the `access` of each `account_permissions` entry.
 */
export const withoutAccountScopedPermissions = (
  permissions: API.Permission[]
): API.Permission[] =>
  permissions.filter(
    (permission) => !ACCOUNT_SCOPED_MODEL_STRS.has(permission.model_str)
  );

/**
 * Accounts whose permissions can apply to accounts opened later: selected and set to Move money.
 * A View only account has no permissions to copy, and the server requires at least one.
 */
export const getFutureAccountReferenceOptions = (
  formData: Pick<RoleFormData, "accounts" | "accountPermissions">
) =>
  formData.accounts.filter(
    (account) =>
      account.selected &&
      getAccountPermissionState(formData.accountPermissions, account.id)
        .access === "move_money"
  );

/**
 * The server keeps only the permissions for accounts opened later, not which account they came
 * from, so a saved role picks the first account that has exactly those permissions. When none
 * does, nothing is picked and the form asks for one.
 */
const findFutureAccountReferenceId = (
  savedPermissions: string[],
  formData: Pick<RoleFormData, "accounts" | "accountPermissions">
): string => {
  const saved = new Set(savedPermissions);
  const match = getFutureAccountReferenceOptions(formData).find((account) => {
    const { permissions } = getAccountPermissionState(
      formData.accountPermissions,
      account.id
    );
    return (
      permissions.length === saved.size &&
      permissions.every((permission) => saved.has(permission))
    );
  });
  return match ? String(match.id) : "";
};

export const buildInitialFormData = (options: {
  role?: Role | null;
  accounts: any[];
  externalAccounts: any[];
  defaultLimits: ReturnType<typeof getDefaultLimits>;
  features?: Record<string, any>;
  isDuplicate?: boolean;
}): RoleFormData => {
  const {
    role,
    accounts,
    externalAccounts,
    defaultLimits,
    features,
    isDuplicate = false,
  } = options;
  const organizationSecCodes = getOrganizationSecCodes(features);

  const initialData: RoleFormData = {
    // For duplicate flow, always start with empty name/description
    // For create/edit, pre-fill from role (if editing)
    role_name: isDuplicate ? "" : role?.name ?? "",
    role_description: isDuplicate ? "" : role?.description ?? "",
    accounts: role ? role.markSelectedAccounts(accounts) : accounts,
    externalAccounts: role
      ? role.markSelectedAccounts(externalAccounts)
      : externalAccounts,
    extraPermissions: {},
    accountPermissions: buildAccountPermissionState(role, accounts),
    futureAccounts: DEFAULT_FUTURE_ACCOUNTS_STATE,
    ach_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "ach_push");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.ach;
    })(),
    wire_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "wire");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.wire;
    })(),
    ach_payment_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "unverified_ach_push");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.achPayment;
    })(),
    collect_ach_payment_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "unverified_ach_pull");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.collectAchPayment;
    })(),
    fednow_unverified_send_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "fednow_unverified");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.fednowUnverifiedSend;
    })(),
    international_wire: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "international_wire");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.internationalWire;
    })(),
    ach_passthrough_send_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "ach_passthrough_send");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.achPassthroughSend;
    })(),
    ach_passthrough_collect_limits: (() => {
      if (role?.limits) {
        const amount = getLimitAmount(role.limits, "ach_passthrough_collect");
        if (amount > 0) return formatNumber(amount.toString());
      }
      return defaultLimits.achPassthroughCollect;
    })(),
    secCodeLimits: {
      push: Object.fromEntries(
        organizationSecCodes.push.map((secCode) => {
          const roleLimits = (role?.limits as Record<string, any> | undefined)?.[
            SEC_CODE_LIMIT_KEYS.push
          ];
          const roleAmount = getLimitAmount(
            roleLimits,
            secCode
          );
          const organizationAmount = getLimitAmount(
            features?.limits?.[SEC_CODE_LIMIT_KEYS.push],
            secCode
          );
          const amount = roleAmount || organizationAmount;
          return [secCode, amount ? formatNumber(amount.toString()) : ""];
        })
      ),
      pull: Object.fromEntries(
        organizationSecCodes.pull.map((secCode) => {
          const roleLimits = (role?.limits as Record<string, any> | undefined)?.[
            SEC_CODE_LIMIT_KEYS.pull
          ];
          const roleAmount = getLimitAmount(
            roleLimits,
            secCode
          );
          const organizationAmount = getLimitAmount(
            features?.limits?.[SEC_CODE_LIMIT_KEYS.pull],
            secCode
          );
          const amount = roleAmount || organizationAmount;
          return [secCode, amount ? formatNumber(amount.toString()) : ""];
        })
      ),
    },
    secCodeLimitEnabled: {
      push: Object.fromEntries(
        organizationSecCodes.push.map((secCode) => {
          const roleLimits = (role?.limits as Record<string, any> | undefined)?.[
            SEC_CODE_LIMIT_KEYS.push
          ];
          const hasRoleLimit =
            roleLimits && Object.prototype.hasOwnProperty.call(roleLimits, secCode);
          const amount = hasRoleLimit
            ? getLimitAmount(roleLimits, secCode)
            : getLimitAmount(
                features?.limits?.[SEC_CODE_LIMIT_KEYS.push],
                secCode
              );
          return [secCode, amount > 0];
        })
      ),
      pull: Object.fromEntries(
        organizationSecCodes.pull.map((secCode) => {
          const roleLimits = (role?.limits as Record<string, any> | undefined)?.[
            SEC_CODE_LIMIT_KEYS.pull
          ];
          const hasRoleLimit =
            roleLimits && Object.prototype.hasOwnProperty.call(roleLimits, secCode);
          const amount = hasRoleLimit
            ? getLimitAmount(roleLimits, secCode)
            : getLimitAmount(
                features?.limits?.[SEC_CODE_LIMIT_KEYS.pull],
                secCode
              );
          return [secCode, amount > 0];
        })
      ),
    },
  };

  if (role?.accounts_opened_later?.enabled) {
    initialData.futureAccounts = {
      enabled: true,
      referenceId: findFutureAccountReferenceId(
        role.accounts_opened_later.permissions,
        initialData
      ),
    };
  }

  // Initialize extra permissions based on role
  OrganizationAccessLevelExtraPermissions.affectedPermissions.forEach(
    (model: string) => {
      const found = role
        ? role.permissions.find((p) => {
            // Only write grants load as checked. Read-only role-level grants (e.g. leaked from
            // switching a Collaborator to Viewer in the legacy flow) load unchecked and are dropped
            // on save; the user can grant them again explicitly.
            const isWriteOperation = ["*", "write"].includes(p.operation);
            if (p.model_str !== model || !isWriteOperation) return false;
            if (
              p.model_str !== MODEL_STR.EXTERNAL_ACCOUNT &&
              p.model_str !== MODEL_STR.ACCOUNT
            )
              return true;
            return p.uuid === ALL;
          })
        : undefined;

      initialData[model] = Boolean(found);
      initialData.extraPermissions[model] = Boolean(found);
    }
  );

  // The server stores a converted role's account access as one permission per account, not the
  // `*` wildcard, so the extra-permission loop above can't tell "all accounts". Derive the flag from
  // the selection, as `reconcileAccountFields` does, so the permissions that depend on it (cards,
  // statements) aren't cleared on the first render.
  const allAccountsSelected =
    initialData.accounts.length > 0 &&
    initialData.accounts.every((account: any) => account.selected);
  if (allAccountsSelected) {
    initialData[MODEL_STR.ACCOUNT] = true;
    initialData.extraPermissions[MODEL_STR.ACCOUNT] = true;
  }

  return initialData;
};

/**
 * Permissions the role grants anywhere: the role-level ones plus the account-scoped ones
 * (money movement, manage templates) checked on any account the role can move money in.
 * Limits apply to a rail whichever of the two grants it. Account-scoped permissions only count
 * through `accountPermissions`: the top-level flags for them are seeded from the role's flat
 * permissions when the form loads and the account editor never updates them.
 */
const getGrantedPermissions = (
  formData: RoleFormData,
  roleLevelPermissions: string[]
): Set<string> => {
  const granted = new Set(roleLevelPermissions);
  formData.accounts
    .filter((account) => account.selected)
    .forEach((account) => {
      const accountPermissionstate =
        formData.accountPermissions?.[String(account.id)];
      if (accountPermissionstate?.access !== "move_money") return;
      accountPermissionstate.permissions.forEach((modelStr) =>
        granted.add(modelStr)
      );
    });
  return granted;
};

export interface LimitRail {
  /** Form field holding the limit as the user typed it. */
  field: string;
  /** Key in `role.limits` and the organization's `features.limits`. */
  limitKey: string;
  /** The rail is enabled when the role is granted any of these on at least one account. */
  modelStrs: string[];
  /** ACH push needs an external account to send to. */
  needsExternalAccount?: boolean;
}

/** Payment rails with a daily limit, in the order the limits table lists them. */
export const LIMIT_RAILS: LimitRail[] = [
  {
    field: "ach_payment_limits",
    limitKey: "unverified_ach_push",
    modelStrs: [
      MODEL_STR.ACH_PAYMENT,
      MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE,
      MODEL_STR.ACH_PAYMENT_VIA_IMPORT,
    ],
  },
  {
    field: "collect_ach_payment_limits",
    limitKey: "unverified_ach_pull",
    modelStrs: [
      MODEL_STR.COLLECT_ACH_PAYMENT,
      MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE,
      MODEL_STR.ACH_PAYMENT_COLLECT_VIA_IMPORT,
    ],
  },
  {
    field: "fednow_unverified_send_limits",
    limitKey: "fednow_unverified",
    modelStrs: [MODEL_STR.UNVERIFIED_FEDNOW_SEND],
  },
  {
    field: "ach_passthrough_send_limits",
    limitKey: "ach_passthrough_send",
    modelStrs: [MODEL_STR.ACH_PASSTHROUGH_SEND],
  },
  {
    field: "ach_passthrough_collect_limits",
    limitKey: "ach_passthrough_collect",
    modelStrs: [MODEL_STR.ACH_PASSTHROUGH_COLLECT],
  },
  {
    field: "ach_limits",
    limitKey: "ach_push",
    modelStrs: [MODEL_STR.ACH_TRANSFER],
    needsExternalAccount: true,
  },
  {
    field: "wire_limits",
    limitKey: "wire",
    modelStrs: [
      MODEL_STR.DOMESTIC_WIRE_TRANSACTION,
      MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES,
      MODEL_STR.DOMESTIC_WIRE_IMPORT,
    ],
  },
  {
    field: "international_wire",
    limitKey: "international_wire",
    modelStrs: [
      MODEL_STR.INTERNATIONAL_WIRE,
      MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES,
    ],
  },
];

const getRoleLevelPermissions = (formData: RoleFormData): string[] => {
  const extraPermissions = bundleExtraPermissions(formData) as Record<
    string,
    boolean
  >;
  // Account-scoped flags are left out: see `getGrantedPermissions`.
  return Object.keys(extraPermissions).filter(
    (modelStr) =>
      extraPermissions[modelStr] && !ACCOUNT_SCOPED_MODEL_STRS.has(modelStr)
  );
};

/** Account-level and role-level permissions the form grants. */
const getFormGrantedPermissions = (formData: RoleFormData) =>
  getGrantedPermissions(formData, getRoleLevelPermissions(formData));

export const canMakeExternalTransfers = (formData: RoleFormData) =>
  getFormGrantedPermissions(formData).has(MODEL_STR.ACH_TRANSFER);

export const allExternalAccountsSelected = (formData: {
  externalAccounts: { selected?: boolean }[];
}) => formData.externalAccounts.every((account) => account.selected);

/** Codes `validateExternalAccounts` can return; `RoleExternalAccountsField` renders the banner. */
export const EXTERNAL_ACCOUNTS_ERROR = {
  NONE_SELECTED: "none-selected",
} as const;

/**
 * Applies the rules that tie the external accounts to the account-level permissions. `formData` is
 * the form as it is now and `change` the update being made; the result is `change` plus whatever
 * has to change with it, so it all lands in one update (see `reconcileAccountFields`).
 * - The selection only applies while some account can make external transfers, so it is cleared
 *   (and the list hidden) when none can.
 * - "Add external accounts" saves as a grant for every external account, so it can't stay on
 *   unless all of them are selected. This is the legacy manage users rule.
 */
export const reconcileExternalAccountFields = (
  formData: Record<string, any>,
  change: Record<string, any>
): Record<string, any> => {
  const next = { ...formData, ...change } as RoleFormData;
  if (!Array.isArray(next.externalAccounts)) return change;
  const result = { ...change };
  const canTransfer = canMakeExternalTransfers(next);
  if (
    !canTransfer &&
    next.externalAccounts.some((account) => account.selected)
  ) {
    result.externalAccounts = next.externalAccounts.map((account) => {
      const cleared = new Account(account);
      (cleared as any).selected = false;
      return cleared;
    });
  }
  const externalAccounts = result.externalAccounts ?? next.externalAccounts;
  if (
    next[MODEL_STR.EXTERNAL_ACCOUNT] &&
    !(canTransfer && allExternalAccountsSelected({ externalAccounts }))
  ) {
    result[MODEL_STR.EXTERNAL_ACCOUNT] = false;
  }
  return result;
};

/** "Manage BillPay recipients" is disabled without "Pay bills via BillPay", so it can't stay on without it. */
export const reconcilePaymentFields = (
  formData: Record<string, any>,
  change: Record<string, any>
): Record<string, any> => {
  const payment =
    MODEL_STR.PAYMENT in change
      ? change[MODEL_STR.PAYMENT]
      : formData[MODEL_STR.PAYMENT];
  return payment ? change : { ...change, [MODEL_STR.PAYEE]: false };
};

/** Codes `validateFutureAccounts` can return; `RoleFutureAccountsField` renders the error. */
export const FUTURE_ACCOUNTS_ERROR = {
  NO_REFERENCE: "no-reference",
} as const;

/**
 * Clears the account picked for accounts opened later when it can no longer be used: the option was
 * unchecked, or the account was deselected or set to View only. The form then asks for another.
 */
export const reconcileFutureAccountFields = (
  formData: Record<string, any>,
  change: Record<string, any>
): Record<string, any> => {
  const next = { ...formData, ...change } as RoleFormData;
  const { futureAccounts } = next;
  if (!futureAccounts?.referenceId || !Array.isArray(next.accounts)) {
    return change;
  }
  const isUsable =
    futureAccounts.enabled &&
    getFutureAccountReferenceOptions(next).some(
      (account) => String(account.id) === futureAccounts.referenceId
    );
  return isUsable
    ? change
    : { ...change, futureAccounts: { ...futureAccounts, referenceId: "" } };
};

/** All reconcile rules for a change to the role form. */
export const reconcileRoleFormData = (
  formData: Record<string, any>,
  change: Record<string, any>
): Record<string, any> =>
  reconcileFutureAccountFields(
    formData,
    reconcilePaymentFields(
      formData,
      reconcileExternalAccountFields(formData, reconcileAccountFields(change))
    )
  );

/** With "Access to all accounts opened later" checked, an account to copy permissions from is required. */
export const validateFutureAccounts = (value: FutureAccountsState) =>
  value.enabled && !value.referenceId
    ? FUTURE_ACCOUNTS_ERROR.NO_REFERENCE
    : null;

/**
 * Builds `accounts_opened_later` for the create and update bodies: the picked account's permissions
 * as they are when the role is saved. The role keeps that copy; changing the account later doesn't
 * change it. Call it after `validateFutureAccounts` passes: with the option checked, an account
 * must be picked.
 */
export const buildAccountsOpenedLaterPayload = (
  formData: Pick<RoleFormData, "accountPermissions" | "futureAccounts">
): API.AccountsOpenedLater => {
  const { enabled, referenceId } = formData.futureAccounts;
  if (!enabled) return { enabled: false, permissions: [] };
  if (!referenceId) {
    throw new Error("No account picked for accounts opened later");
  }
  const { permissions } = getAccountPermissionState(
    formData.accountPermissions,
    referenceId
  );
  return { enabled: true, permissions: [...permissions].sort() };
};

/**
 * With some account able to make external transfers, a role needs at least one external account to
 * send to (when the organization has any).
 */
export const validateExternalAccounts = (
  value: { selected?: boolean }[],
  data: RoleFormData
) =>
  canMakeExternalTransfers(data) &&
  value.length > 0 &&
  !value.some((account) => account.selected)
    ? EXTERNAL_ACCOUNTS_ERROR.NONE_SELECTED
    : null;

/**
 * Form fields of the rails the role can use: granted on at least one account (or role-wide).
 * The rest save a limit of zero and show as not enabled in the limits table.
 */
export const getEnabledLimitFields = (formData: RoleFormData): Set<string> => {
  const roleLevelPermissions = getRoleLevelPermissions(formData);
  const granted = getGrantedPermissions(formData, roleLevelPermissions);
  // Needs an external account to send to: a selected one, or none to choose from and the
  // permission to add one.
  const canPushToExternalAccount = formData.externalAccounts.length
    ? formData.externalAccounts.some((account) => account.selected)
    : roleLevelPermissions.includes(MODEL_STR.EXTERNAL_ACCOUNT);
  return new Set(
    LIMIT_RAILS.filter(
      (rail) =>
        rail.modelStrs.some((modelStr) => granted.has(modelStr)) &&
        (!rail.needsExternalAccount || canPushToExternalAccount)
    ).map((rail) => rail.field)
  );
};

export type LimitError = { type: "zero" } | { type: "max"; max: number };

/**
 * A limit has to be more than zero and within the organization's limit for the rail, as in the
 * legacy manage users form.
 */
export const getLimitError = (
  rail: LimitRail,
  value: string | undefined,
  features: Record<string, any> | undefined
): LimitError | null => {
  const amount = parseLimitValue(value ?? "");
  if (amount <= 0) return { type: "zero" };
  const max = features?.limits?.[rail.limitKey]?.[1];
  if (max && amount > max) return { type: "max", max };
  return null;
};

export const getSecCodeLimitErrors = (
  formData: RoleFormData,
  features: Record<string, any> | undefined
): {
  direction: SecCodeDirection;
  secCode: string;
  rail: LimitRail;
  error: LimitError;
}[] => {
  const enabledFields = getEnabledLimitFields(formData);
  return (["push", "pull"] as const).flatMap((direction) => {
    const rail = LIMIT_RAILS.find(
      (limitRail) => limitRail.limitKey === SEC_CODE_RAILS[direction]
    );
    if (!rail || !enabledFields.has(rail.field)) return [];

    return Object.entries(formData.secCodeLimits?.[direction] ?? {}).flatMap(
      ([secCode, value]) => {
        if (!formData.secCodeLimitEnabled?.[direction]?.[secCode]) return [];
        const amount = parseLimitValue(value);
        const max =
          features?.limits?.[SEC_CODE_LIMIT_KEYS[direction]]?.[secCode]?.[1];
        let error: LimitError | null = null;
        if (amount <= 0) error = { type: "zero" };
        else if (max !== undefined && max !== null && amount > Number(max))
           error = { type: "max", max: Number(max) };
        return error ? [{ direction, secCode, rail, error }] : [];
      }
    );
  });
};

export const buildRoleFromFormData = (
  formData: RoleFormData,
  savedLimits: Record<string, Record<string, unknown>> = {}
): {
  role: Role;
  limits: Record<string, any>;
} => {
  // Compute the flat set of granted permissions based on role-level and account-scoped permissions
  const granted = getGrantedPermissions(
    formData,
    getRoleLevelPermissions(formData)
  );

  // "Add external accounts" is a grant for every external account, so it only counts while some
  // account can make external transfers and all external accounts are selected. The form clears it
  // otherwise (see `reconcileExternalAccountFields`); this keeps a stale flag from ever widening
  // the role.
  const canAddExternalAccounts =
    granted.has(MODEL_STR.ACH_TRANSFER) &&
    allExternalAccountsSelected(formData);
  const roleLevelPermissions = getRoleLevelPermissions(formData).filter(
    (modelStr) =>
      modelStr !== MODEL_STR.EXTERNAL_ACCOUNT || canAddExternalAccounts
  );

  const selectedExternalAccounts = formData.externalAccounts.filter(
    (externalAccount) => externalAccount.selected
  );

  const role = new Role({ name: "Custom role" });

  // Add role-level permissions
  roleLevelPermissions.forEach((modelStr) => {
    role.permissions.push({
      model_str: modelStr,
      operation: "write",
      uuid: ALL,
    });
  });

  // Add access to each selected account
  formData.accounts
    .filter((account) => account.selected)
    .forEach((account) => {
      role.permissions.push({
        model_str: MODEL_STR.ACCOUNT,
        operation: "write",
        uuid: account.id,
      });
    });

  // Individual external accounts are separate from the permission to add, edit or remove external
  // accounts, and only matter when the role can make external transfers from some account.
  if (granted.has(MODEL_STR.ACH_TRANSFER)) {
    selectedExternalAccounts.forEach((externalAccount) => {
      role.permissions.push({
        model_str: MODEL_STR.EXTERNAL_ACCOUNT,
        operation: "write",
        uuid: externalAccount.id,
      });
    });
  }

  const enabledFields = getEnabledLimitFields(formData);
  role.limits = Object.fromEntries(
    LIMIT_RAILS.map((rail) => {
      const daily = enabledFields.has(rail.field)
        ? parseLimitValue(formData[rail.field])
        : 0;
      const windows = Object.entries(savedLimits[rail.limitKey] ?? {}).filter(
        ([, amount]) => Number(amount) >= daily
      );
      return [rail.limitKey, { ...Object.fromEntries(windows), 1: daily }];
    })
  ) as unknown as API.Limits.ByType;
  const roleLimits: Record<string, any> = {
    ...savedLimits,
    ...role.limits,
  };
  (["push", "pull"] as const).forEach((direction) => {
    const secCodeValues = formData.secCodeLimits?.[direction] ?? {};
    if (Object.keys(secCodeValues).length === 0) return;

    const rail = LIMIT_RAILS.find(
      (limitRail) => limitRail.limitKey === SEC_CODE_RAILS[direction]
    );
    const isEnabled = rail && enabledFields.has(rail.field);
    const savedSecCodeLimits =
      savedLimits[SEC_CODE_LIMIT_KEYS[direction]] ?? {};
    roleLimits[SEC_CODE_LIMIT_KEYS[direction]] = {
      ...savedSecCodeLimits,
      ...Object.fromEntries(
        Object.entries(secCodeValues).map(([secCode, value]) => {
          const daily =
            isEnabled && formData.secCodeLimitEnabled?.[direction]?.[secCode]
              ? parseLimitValue(value)
              : 0;
          const savedPeriods = savedSecCodeLimits[secCode];
          const windows = Object.entries(
            savedPeriods !== null && typeof savedPeriods === "object"
              ? savedPeriods
              : {}
          ).filter(([, amount]) => Number(amount) >= daily);
          return [secCode, { ...Object.fromEntries(windows), 1: daily }];
        })
      ),
    };
  });
  role.limits = roleLimits as API.Limits.ByType;

  return { role, limits: roleLimits };
};

const LIMIT_FIELDS = LIMIT_RAILS.map((rail) => rail.field);

const selectedIds = (accounts: { id?: unknown; selected?: boolean }[]) =>
  accounts
    .filter((account) => account.selected)
    .map((account) => String(account.id))
    .sort();

/** Only what a save would send, in a stable order, so equal configurations compare equal. */
const toComparableFormData = (formData: RoleFormData) => {
  const accountIds = selectedIds(formData.accounts);
  return {
    name: formData.role_name,
    description: formData.role_description,
    accounts: accountIds,
    externalAccounts: selectedIds(formData.externalAccounts),
    // Settings of an account the role can't access are not saved.
    accountPermissions: accountIds.map((accountId) => {
      const state = getAccountPermissionState(
        formData.accountPermissions,
        accountId
      );
      // Like the saved payload: only Move money accounts keep permissions. The editor remembers
      // checked boxes when switching to View only, which doesn't change what is saved.
      const permissions =
        state.access === "move_money" ? [...state.permissions].sort() : [];
      return [accountId, state.access, permissions];
    }),
    // The checkbox and the picked account, not the payload: the payload can't be built while the
    // option is checked with nothing picked, which is how a role loads when no account matches.
    futureAccounts: formData.futureAccounts.enabled
      ? [true, formData.futureAccounts.referenceId]
      : [false],
    roleLevelPermissions:
      OrganizationAccessLevelExtraPermissions.affectedPermissions.filter(
        (modelStr: string) => Boolean(formData[modelStr])
      ),
    limits: LIMIT_FIELDS.map((field) => formData[field]),
    secCodeLimits: (["push", "pull"] as const).map((direction) =>
      Object.entries(formData.secCodeLimits?.[direction] ?? {}).sort(
        ([codeA], [codeB]) => codeA.localeCompare(codeB)
      )
    ),
    secCodeLimitEnabled: (["push", "pull"] as const).map((direction) =>
      Object.entries(formData.secCodeLimitEnabled?.[direction] ?? {}).sort(
        ([codeA], [codeB]) => codeA.localeCompare(codeB)
      )
    ),
  };
};

/**
 * True when the form differs from `saved` in anything that would be saved. `saved` must be a copy
 * taken when the form loaded: `ContextForm` edits the form data object in place.
 */
export const isRoleFormDirty = (
  saved: RoleFormData,
  current: RoleFormData
): boolean =>
  JSON.stringify(toComparableFormData(saved)) !==
  JSON.stringify(toComparableFormData(current));

/**
 * Rails the institution offers, in the order the limits table lists them. A rail is offered when
 * the account editor offers at least one of its permissions, so a rail never shows a row that can't
 * be enabled (e.g. instant delivery needs several features, not just FedNow).
 */
export const getAvailableLimitRails = (
  features: Record<string, any> | undefined
): LimitRail[] => {
  const availableModelStrs = new Set(
    ACCOUNT_PERMISSION_OPTIONS.filter((option) =>
      option.isAvailable(features ?? {})
    ).map((option) => option.modelStr)
  );
  return LIMIT_RAILS.filter((rail) =>
    rail.modelStrs.some((modelStr) => availableModelStrs.has(modelStr))
  );
};

/** Limits that block saving: only rails the role can use are checked, as the rest save as zero. */
export const getLimitErrors = (
  formData: RoleFormData,
  features: Record<string, any> | undefined
): { rail: LimitRail; error: LimitError }[] => {
  const enabledFields = getEnabledLimitFields(formData);
  return getAvailableLimitRails(features)
    .filter((rail) => enabledFields.has(rail.field))
    .flatMap((rail) => {
      const error = getLimitError(rail, formData[rail.field], features);
      return error ? [{ rail, error }] : [];
    });
};

/** Returned by `validateRoleLimits`; `RoleDailyLimitsSection` lists the limits that failed. */
export const LIMITS_ERROR = "invalid-limits";

export const validateRoleLimits = (
  formData: RoleFormData,
  features: Record<string, any> | undefined
) =>
  getLimitErrors(formData, features).length > 0 ||
  getSecCodeLimitErrors(formData, features).length > 0
    ? LIMITS_ERROR
    : null;
